<template>
  <v-container class="pa-3">
    <v-row wrap>
      <v-col cols="12">
        <v-card
          v-motion
          :initial="{ y: 30 }"
          :enter="{ y: 0 }"
          class="px-6 gentle-grad text-white d-flex justify-start align-center"
        >
          <background-particles />

          <div class="d-flex justify-space-between align-center">
            <h1 class="text-h5 text-left">
              Welcome {{ userStore.basic.fName }}!
            </h1>
            <Vue3Lottie
              :animationData="WalkingJSON"
              :width="100"
              :height="100"
            />
          </div>
        </v-card>
      </v-col>
      <v-col cols="12">
        <BankSwitcher />
      </v-col>

      <v-col cols="12" v-if="announcementsForCurrentBank.length">
        <AnnouncementCarousel
          :announcements="announcementsForCurrentBank"
        />
      </v-col>

      <!-- Subscription box -->
      <v-col
        cols="12"
        v-if="
          bankStore.currentBank &&
          paymentStore.isSubscribed !== undefined &&
          !paymentStore.isSubscribed?.[bankStore.currentBank]?.val
        "
      >
        <v-card
          v-motion
          :initial="{ y: 30 }"
          :enter="{ y: 0 }"
          :delay="100"
          class="pa-6"
        >
          <v-alert
            class="my-3 text-caption"
            icon="mdi-credit-card-outline"
            type="success"
            variant="tonal"
            v-motion
            :initial="{ opacity: 0, x: 10 }"
            :enter="{ opacity: 1, x: 0, scale: 1 }"
            :delay="175"
          >
            <p class="mt-1 text-body-2 font-weight-bold">
              You are currently not subscribed to the
              {{ getBankDisplayName(bankStore.currentBank) }}. You can create 1
              session to try out our platform.
            </p>
            <p class="mt-1">
              You can purchase a subscription on the main
              <a href="https://eye.academy/" target="_blank"
                >Eye Academy website</a
              >.
            </p>
          </v-alert>
        </v-card>
      </v-col>

      <!-- Sessions examining for interview and video bank -->
      <v-col cols="12" v-if="dataStore.sessionsUserIsExaminerData.length !== 0">
        <v-card
          v-motion
          :initial="{ y: 30 }"
          :enter="{ y: 0 }"
          :delay="200"
          class="pa-12 bg-slightBlue"
        >
          <v-row class="px-sm-6 text-caption">
            <v-col cols="12" class="text-center">
              <div class="text-body-1 font-weight-bold mb-4">
                Sessions You're Examining
              </div>
            </v-col>

            <v-col
              cols="12"
              v-for="session in dataStore.sessionsUserIsExaminerData"
              :key="session.id"
            >
              <div
                class="d-flex flex-column flex-sm-row align-sm-center justify-space-between pa-4 rounded-lg mt-3"
                style="border: 1px solid black"
              >
                <div>
                  <div class="text-body-2">{{ session.userEmail }}</div>
                  <div class="text-caption text-disabled">
                    Created: {{ easyToReadDate(session.createdAt.seconds) }}
                  </div>
                  <div class="text-caption text-disabled">
                    Number of Stations:
                    {{
                      session.stationCount ??
                      session.stationIds?.length ??
                      session.stations?.length
                    }}
                  </div>
                </div>
                <v-btn
                  @click="
                    $router.push(
                      `/sessions/${bankStore.currentBank}/${session.id}`,
                    )
                  "
                  color="primary"
                  variant="flat"
                  size="small"
                  rounded="lg"
                  class="mt-3 mt-sm-0"
                >
                  View Session
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Exam date -->
      <v-col v-if="secondsLeftTillExam" cols="12">
        <v-card v-motion :initial="{ y: 30 }" :enter="{ y: 0 }" :delay="100">
          <vue-countdown
            :time="secondsLeftTillExam"
            v-slot="{ days, hours, minutes, seconds }"
          >
            <v-row class="px-12 py-3">
              <v-col
                cols="12"
                class="text-center"
                v-motion
                :initial="{ y: 50 }"
                :enter="{ y: 0 }"
              >
                <div
                  class="text-body-1 font-weight-bold d-flex align-center justify-center"
                >
                  Exam Day Countdown
                </div>
                <div class="text-caption text-disabled">
                  {{ examDateUKFormat }}
                </div>
                <div v-if="!editingExamDate">
                  <v-btn
                    size="x-small"
                    variant="flat"
                    prepend-icon="mdi-pencil"
                    @click="editingExamDate = true"
                  >
                    Edit
                  </v-btn>
                </div>
                <div v-else class="mt-4">
                  <v-text-field
                    class="px-md-12 text-left"
                    v-model="examDate"
                    label="Exam Date"
                    type="date"
                    persistent-hint
                    :hint="examDateHint"
                    @change="examDateNotValid(examDate)"
                  ></v-text-field>
                  <v-btn
                    class="mx-2"
                    variant="flat"
                    prepend-icon="mdi-check"
                    @click="changeExamDate"
                    color="success"
                    :disabled="!examDateValid"
                    :loading="examDateUpdateLoading"
                  >
                    submit
                  </v-btn>
                  <v-btn
                    variant="flat"
                    prepend-icon="mdi-cancel"
                    @click="editingExamDate = false"
                    color="error"
                  >
                    Cancel
                  </v-btn>
                </div>
              </v-col>
              <v-col
                v-for="(time, idx) in [
                  [days, 'day', 365],
                  [hours, 'hour', 24],
                  [minutes, 'minute', 60],
                  [seconds, 'second', 60],
                ]"
                :key="idx"
                cols="6"
                md="3"
                class="text-center"
                v-motion
                :initial="{ y: 50 }"
                :enter="{ y: 0 }"
                :delay="idx * 25"
              >
                <v-progress-circular
                  :rotate="360"
                  :size="70"
                  :width="2"
                  color="progress"
                  :model-value="(time[0] / time[2]) * 100"
                >
                  <strong>{{ time[0] }}</strong>
                </v-progress-circular>
                <br />
                <div class="text-body-1 mt-3">
                  {{ time[0] !== 1 ? time[1] + "s" : time[1] }}
                </div>
              </v-col>
            </v-row>
          </vue-countdown>
        </v-card>
      </v-col>

      <!-- Nomograms (peer comparison) -->
      <v-col cols="12" v-if="bankStore.currentBank">
        <NomogramPanel :bank-id="bankStore.currentBank" />
      </v-col>

      <!-- Quick stats for part 1/2 bank -->
      <v-col
        cols="12"
        v-if="
          bankStore.currentBank === 'part-2-question-bank' ||
          bankStore.currentBank === 'part-1-question-bank' ||
          bankStore.currentBank === 'duke-elder-question-bank'
        "
      >
        <v-card
          v-motion
          :initial="{ y: 30 }"
          :enter="{ y: 0 }"
          :delay="100"
          class="pa-6"
        >
          <v-row align="center">
            <v-col cols="12" class="text-center">
              <div class="text-body-1 font-weight-bold">Quick Stats</div>
            </v-col>
            <v-col cols="12" md="4" offset-md="2" class="text-center">
              <div class="text-caption mb-3">Completion Rate</div>
              <v-progress-circular
                :size="100"
                :width="10"
                color="progress"
                :model-value="
                  perc(
                    statsStore.userStats?.uniqueQuestionsAttempted,
                    statsStore.metaStats?.totalQuestions,
                  )
                "
                v-motion
                :initial="{ x: -50 }"
                :enter="{ x: 0 }"
              >
                <strong>
                  {{
                    perc(
                      statsStore.userStats?.uniqueQuestionsAttempted,
                      statsStore.metaStats?.totalQuestions,
                    )
                  }}%</strong
                >
              </v-progress-circular>
            </v-col>
            <v-col cols="12" md="4" class="text-caption">
              <v-table density="compact">
                <thead>
                  <tr>
                    <th class="text-left">-</th>
                    <th class="text-left">#</th>
                    <th class="text-left">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Attempted</td>
                    <td>
                      {{ statsStore.userStats?.uniqueQuestionsAttempted }}
                    </td>
                    <td>
                      {{
                        perc(
                          statsStore.userStats?.uniqueQuestionsAttempted,
                          statsStore.metaStats?.totalQuestions,
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Unattempted</td>
                    <td>
                      {{
                        statsStore.metaStats?.totalQuestions -
                        statsStore.userStats?.uniqueQuestionsAttempted
                      }}
                    </td>
                    <td>
                      {{
                        perc(
                          statsStore.metaStats?.totalQuestions -
                            statsStore.userStats?.uniqueQuestionsAttempted,
                          statsStore.metaStats?.totalQuestions,
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Total</td>
                    <td>
                      {{ statsStore.metaStats?.totalQuestions }}
                    </td>
                    <td>-</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <v-col
              v-if="(statsStore.userStats?.questionsAttempted || 0) > 0"
              cols="12"
              md="4"
              offset-md="2"
              class="text-center"
            >
              <div class="text-caption mb-3">Accuracy Rate</div>
              <v-progress-circular
                :rotate="360"
                :size="100"
                :width="10"
                color="green"
                bg-color="red"
                :model-value="
                  perc(
                    statsStore.userStats?.questionsCorrect,
                    statsStore.userStats?.questionsAttempted,
                  )
                "
                v-motion
                :initial="{ x: -50 }"
                :enter="{ x: 0 }"
              >
                <strong>
                  {{
                    perc(
                      statsStore.userStats?.questionsCorrect,
                      statsStore.userStats?.questionsAttempted,
                    )
                  }}%</strong
                >
              </v-progress-circular>
              <div class="text-caption text-disabled mt-3">
                The site average is
                {{
                  perc(
                    statsStore.metaStats?.totalCorrect,
                    statsStore.metaStats?.totalAttempted,
                  )
                }}%
              </div>
            </v-col>
            <v-col
              v-if="(statsStore.userStats?.questionsAttempted || 0) > 0"
              cols="12"
              md="4"
              class="text-caption"
            >
              <v-table density="compact">
                <thead>
                  <tr>
                    <th class="text-left">-</th>
                    <th class="text-left">#</th>
                    <th class="text-left">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Correct</td>
                    <td>{{ statsStore.userStats?.questionsCorrect }}</td>
                    <td>
                      {{
                        perc(
                          statsStore.userStats?.questionsCorrect,
                          statsStore.userStats?.questionsAttempted,
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Incorrect</td>
                    <td>
                      {{
                        statsStore.userStats?.questionsAttempted -
                        statsStore.userStats?.questionsCorrect
                      }}
                    </td>
                    <td>
                      {{
                        perc(
                          statsStore.userStats?.questionsAttempted -
                            statsStore.userStats?.questionsCorrect,
                          statsStore.userStats?.questionsAttempted,
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Attempted</td>
                    <td>
                      {{ statsStore.userStats?.questionsAttempted }}
                    </td>
                    <td>-</td>
                  </tr>
                </tbody>
              </v-table>
              <div class="text-caption text-center text-disabled">
                <i>Includes repetitions.</i>
              </div>
            </v-col>
            <!-- Category stats for the banks -->
            <v-col
              cols="12"
              md="8"
              offset-md="2"
              class="mt-12"
              v-if="
                bankStore.currentBank === 'part-2-question-bank' ||
                bankStore.currentBank === 'part-1-question-bank' ||
                bankStore.currentBank === 'duke-elder-question-bank'
              "
            >
              <v-row align="center">
                <v-col cols="12" class="text-center">
                  <div class="text-body-1 font-weight-bold mb-4">
                    Category Performance
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-expansion-panels
                    :multiple="parentCats.length === 1"
                    :model-value="parentCats.length === 1 ? [0] : null"
                  >
                    <v-expansion-panel
                      v-for="(pcat, i) in parentCats"
                      :key="pcat.parent"
                    >
                      <v-expansion-panel-title
                        class="min-height: 150px; !important"
                        expand-icon="mdi-chevron-down-circle"
                        collapse-icon="mdi-chevron-up-circle"
                      >
                        <template v-slot:actions="{ expanded }">
                          <v-icon
                            v-if="expanded"
                            :size="mobile ? 'small' : 'x-large'"
                            color="progress"
                            class="mb-5"
                            >mdi-chevron-up-circle</v-icon
                          >
                          <v-icon
                            v-else
                            :size="mobile ? 'small' : 'x-large'"
                            color="progress"
                            class="mb-5"
                            >mdi-chevron-down-circle</v-icon
                          >
                        </template>
                        <v-row no-gutters>
                          <v-col md="4" cols="7" class="align-self-center">
                            {{ pcat.parent }}
                          </v-col>
                          <v-col md="2" cols="5" class="align-self-center">
                            {{ pcat.totalQuestions }} Questions
                          </v-col>
                          <v-col
                            md="6"
                            class="align-self-center hidden-xs pr-6"
                          >
                            <v-progress-linear
                              class="mb-1"
                              :model-value="
                                getChildrenCompletionData(pcat.children).perc
                              "
                              color="progress"
                              rounded
                              height="10"
                            ></v-progress-linear>
                            <span class="text-caption text-disabled">
                              Completed:
                              {{
                                getChildrenCompletionData(pcat.children).perc
                              }}% ({{
                                getChildrenCompletionData(pcat.children)
                                  .completed
                              }}
                              of
                              {{
                                getChildrenCompletionData(pcat.children).total
                              }})
                            </span>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-title>

                      <v-expansion-panel-text>
                        <v-table class="text-caption" density="compact">
                          <thead>
                            <tr>
                              <th class="text-left">Category</th>
                              <th class="text-center">Completion</th>
                              <th class="text-center">Accuracy</th>
                              <th class="text-center">Peer Accuracy</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="cat in pcat.children" :key="cat">
                              <td class="text-left">
                                {{ categoryDataObject[cat].name }}
                              </td>
                              <td class="text-center">
                                <v-chip
                                  size="x-small"
                                  :color="
                                    perc(
                                      getUserCatStat(categoryDataObject[cat].id)
                                        .uniqueQuestionsAttempted,
                                      categoryDataObject[cat].stats
                                        .numberOfQuestions,
                                    ) >= 75
                                      ? 'success'
                                      : perc(
                                          getUserCatStat(
                                            categoryDataObject[cat].id,
                                          ).uniqueQuestionsAttempted,
                                          categoryDataObject[cat].stats
                                            .numberOfQuestions,
                                        ) >= 50
                                      ? 'warning'
                                      : 'error'
                                  "
                                  class="font-weight-bold"
                                >
                                  {{
                                    perc(
                                      getUserCatStat(categoryDataObject[cat].id)
                                        .uniqueQuestionsAttempted,
                                      categoryDataObject[cat].stats
                                        .numberOfQuestions,
                                    )
                                  }}%
                                </v-chip>
                              </td>
                              <td class="text-center">
                                <v-chip
                                  size="x-small"
                                  :color="
                                    perc(
                                      getUserCatStat(categoryDataObject[cat].id)
                                        .questionsCorrect,
                                      getUserCatStat(categoryDataObject[cat].id)
                                        .questionsAttempted,
                                    ) >= 75
                                      ? 'success'
                                      : perc(
                                          getUserCatStat(
                                            categoryDataObject[cat].id,
                                          ).questionsCorrect,
                                          getUserCatStat(
                                            categoryDataObject[cat].id,
                                          ).questionsAttempted,
                                        ) >= 50
                                      ? 'warning'
                                      : 'error'
                                  "
                                  class="font-weight-bold"
                                >
                                  {{
                                    perc(
                                      getUserCatStat(categoryDataObject[cat].id)
                                        .questionsCorrect,
                                      getUserCatStat(categoryDataObject[cat].id)
                                        .questionsAttempted,
                                    )
                                  }}%
                                </v-chip>
                              </td>
                              <td class="text-center">
                                <v-chip
                                  size="x-small"
                                  :color="
                                    perc(
                                      categoryDataObject[cat].stats
                                        .totalCorrectAnswers,
                                      categoryDataObject[cat].stats
                                        .totalQuestionAttempts,
                                    ) >= 75
                                      ? 'success'
                                      : perc(
                                          categoryDataObject[cat].stats
                                            .totalCorrectAnswers,
                                          categoryDataObject[cat].stats
                                            .totalQuestionAttempts,
                                        ) >= 50
                                      ? 'warning'
                                      : 'error'
                                  "
                                  class="font-weight-bold"
                                >
                                  {{
                                    perc(
                                      categoryDataObject[cat].stats
                                        .totalCorrectAnswers,
                                      categoryDataObject[cat].stats
                                        .totalQuestionAttempts,
                                    )
                                  }}%
                                </v-chip>
                              </td>
                            </tr>
                          </tbody>
                        </v-table>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <div
                    v-if="!dataStore.categoriesData.length"
                    class="text-center text-caption pa-4"
                  >
                    No category data available yet. Complete some questions to
                    see your performance breakdown.
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" class="text-center mt-6">
              <v-btn
                @click="$router.push('/sessions/new')"
                color="primary"
                rounded="lg"
                variant="flat"
                size="large"
              >
                Practise Now
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Sessions for part 1/2 bank -->
      <v-col
        cols="12"
        v-if="
          bankStore.currentBank === 'part-2-question-bank' ||
          bankStore.currentBank === 'part-1-question-bank' ||
          bankStore.currentBank === 'duke-elder-question-bank'
        "
      >
        <v-card
          v-motion
          :initial="{ y: 30 }"
          :enter="{ y: 0 }"
          :delay="200"
          class="pa-6"
        >
          <v-row
            v-if="dataStore.sessionsDataOrdered"
            class="px-sm-6 text-caption"
          >
            <v-col cols="12" class="text-center">
              <div class="text-body-1 font-weight-bold">Sessions</div>
            </v-col>
            <v-col cols="12">
              <v-row wrap>
                <v-col cols="6" sm="3"> Created At </v-col>
                <v-col class="hidden-xs" cols="3"> No. Questions </v-col>
                <v-col class="hidden-xs" cols="3"> Status </v-col>
                <v-col class="hidden-xs text-center" cols="3"> Actions </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
            <v-col
              v-if="dataStore.sessionsDataOrdered.length !== 0"
              v-for="(session, idx) in dataStore.sessionsDataOrdered.slice(
                0,
                5,
              )"
              :key="idx"
              cols="12"
            >
              <v-row wrap>
                <v-col cols="6" sm="3">
                  {{ easyToReadDate(session.createdAt.seconds) }}
                </v-col>
                <v-col class="hidden-xs" cols="3">
                  {{ session.numberOfQuestions }} Question{{
                    session.numberOfQuestions !== 1 ? "s" : ""
                  }}
                </v-col>
                <v-col class="hidden-xs" cols="3">
                  <p>
                    {{
                      session.status?.charAt(0)?.toUpperCase() +
                      session.status?.slice(1)
                    }}
                  </p>
                  <p>
                    {{ session.enableTestMode ? "(Test Mode)" : "" }}
                  </p>
                </v-col>
                <v-col cols="6" sm="3" class="text-center">
                  <v-btn
                    @click="
                      $router.push(
                        bankStore.currentBank === 'part-1-question-bank' ||
                          bankStore.currentBank === 'duke-elder-question-bank'
                          ? `/sessions/${bankStore.currentBank}/${session.id}`
                          : `/sessions/${session.id}`,
                      )
                    "
                    color="primary"
                    rounded="lg"
                    variant="flat"
                    size="x-small"
                  >
                    {{ session.status === "completed" ? "View" : "Resume" }}
                  </v-btn>
                  <v-icon
                    @click="
                      openSessionInNewTab(
                        bankStore.currentBank === 'part-1-question-bank' ||
                          bankStore.currentBank === 'duke-elder-question-bank'
                          ? `/sessions/${bankStore.currentBank}/`
                          : '/sessions/',
                        session.id,
                      )
                    "
                    class="ml-2"
                    size="x-small"
                    color="progress"
                    icon="mdi-open-in-new"
                  ></v-icon>
                </v-col>
              </v-row>
            </v-col>
            <v-col v-else cols="12">
              <div class="text-caption text-center">No Sessions</div>
            </v-col>
            <v-col cols="12" class="text-center mt-3">
              <v-btn
                @click="$router.push('/sessions')"
                color="primary"
                rounded="lg"
                variant="flat"
                size="large"
              >
                View All Sessions
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col cols="12" class="text-center">
              <div class="text-body-1 font-weight-bold">Sessions</div>
            </v-col>
            <v-col cols="12" class="text-center text-caption mt-12">
              No sessions created yet
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Quick stats for interview/vid bank -->
      <v-col
        cols="12"
        v-if="
          bankStore.currentBank === 'interview-bank' ||
          bankStore.currentBank === 'part-2-video-bank'
        "
      >
        <v-card
          v-motion
          :initial="{ y: 30 }"
          :enter="{ y: 0 }"
          :delay="100"
          class="pa-6"
        >
          <v-row align="center">
            <v-col cols="12" class="text-center">
              <div class="text-body-1 font-weight-bold">Quick Stats</div>
            </v-col>
            <!-- Completion Rate Section -->
            <v-col cols="12" md="4" offset-md="2" class="text-center">
              <div class="text-caption mb-3">Completion Rate</div>
              <v-progress-circular
                :size="100"
                :width="10"
                color="progress"
                :model-value="
                  perc(
                    dataStore.interviewSessionsStats.stats?.seen,
                    dataStore.interviewSessionsStats.stats?.total,
                  )
                "
                v-motion
                :initial="{ x: -50 }"
                :enter="{ x: 0 }"
              >
                <strong>
                  {{
                    perc(
                      dataStore.interviewSessionsStats.stats?.seen,
                      dataStore.interviewSessionsStats.stats?.total,
                    )
                  }}%
                </strong>
              </v-progress-circular>
            </v-col>
            <v-col cols="12" md="4" class="text-caption">
              <v-table density="compact">
                <thead>
                  <tr>
                    <th class="text-left">-</th>
                    <th class="text-left">#</th>
                    <th class="text-left">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Attempted</td>
                    <td>
                      {{ dataStore.interviewSessionsStats.stats?.seen || 0 }}
                    </td>
                    <td>
                      {{
                        perc(
                          dataStore.interviewSessionsStats.stats?.seen,
                          dataStore.interviewSessionsStats.stats?.total,
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Unattempted</td>
                    <td>
                      {{ dataStore.interviewSessionsStats.stats?.unseen || 0 }}
                    </td>
                    <td>
                      {{
                        perc(
                          dataStore.interviewSessionsStats.stats?.unseen,
                          dataStore.interviewSessionsStats.stats?.total,
                        )
                      }}
                    </td>
                  </tr>
                  <tr>
                    <td>Total</td>
                    <td>
                      {{ dataStore.interviewSessionsStats.stats?.total || 0 }}
                    </td>
                    <td>-</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <!-- Domain stats for interview bank (domain is the titles of the mark scheme)-->
            <v-col
              cols="12"
              md="8"
              offset-md="2"
              class="mt-12"
              v-if="bankStore.currentBank === 'interview-bank'"
            >
              <v-row align="center">
                <v-col cols="12" class="text-center">
                  <div class="text-body-1 font-weight-bold mb-4">
                    Domain Performance
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-table class="text-caption" density="compact">
                    <thead>
                      <tr>
                        <th class="text-left">Domain</th>
                        <th class="text-center">Average Score</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="domain in dataStore.interviewDomainStats.domains"
                        :key="domain.name"
                      >
                        <td class="text-left">{{ domain.name }}</td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              domain.average >= 75
                                ? 'success'
                                : domain.average >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{ domain.average }}%
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-btn
                            size="x-small"
                            variant="text"
                            color="primary"
                            @click="
                              openDomainComments(domain.key || domain.name)
                            "
                            :title="`View comments for ${domain.name}`"
                          >
                            <v-icon size="small"
                              >mdi-comment-text-outline</v-icon
                            >
                            Comments
                          </v-btn>
                        </td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td class="text-left">Overall Average</td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              dataStore.interviewDomainStats.overallAverage >=
                              75
                                ? 'success'
                                : dataStore.interviewDomainStats
                                    .overallAverage >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{ dataStore.interviewDomainStats.overallAverage }}%
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-btn
                            size="x-small"
                            variant="text"
                            color="primary"
                            @click="openDomainComments('all')"
                            title="View all comments"
                          >
                            <v-icon size="small"
                              >mdi-comment-text-outline</v-icon
                            >
                            All Comments
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>

                  <div
                    v-if="dataStore.interviewDomainStats.length === 0"
                    class="text-center text-caption pa-4"
                  >
                    No domain data available yet. Complete some stations to see
                    your performance breakdown.
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <!-- Category stats for video bank -->
            <v-col
              cols="12"
              md="8"
              offset-md="2"
              class="mt-12"
              v-if="bankStore.currentBank === 'part-2-video-bank'"
            >
              <v-row align="center">
                <v-col cols="12" class="text-center">
                  <div class="text-body-1 font-weight-bold mb-4">
                    Category Performance
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-table class="text-caption" density="compact">
                    <thead>
                      <tr>
                        <th class="text-left">Category</th>
                        <th class="text-center">Diagnosis</th>
                        <th class="text-center">Examination</th>
                        <th class="text-center">Aggregate</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="stat in dataStore.videoBankCategoryStats
                          .categories"
                        :key="stat.category"
                      >
                        <td class="text-left">{{ stat.category }}</td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              stat.diagnosis === null ||
                              stat.diagnosis === undefined
                                ? undefined
                                : stat.diagnosis >= 75
                                ? 'success'
                                : stat.diagnosis >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{
                              stat.diagnosis === null ||
                              stat.diagnosis === undefined
                                ? "–"
                                : stat.diagnosis + "%"
                            }}
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              stat.examination === null ||
                              stat.examination === undefined
                                ? undefined
                                : stat.examination >= 75
                                ? 'success'
                                : stat.examination >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{
                              stat.examination === null ||
                              stat.examination === undefined
                                ? "–"
                                : stat.examination + "%"
                            }}
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              stat.aggregate >= 75
                                ? 'success'
                                : stat.aggregate >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{ stat.aggregate }}%
                          </v-chip>
                        </td>
                        <td class="text-center">-</td>
                      </tr>
                      <tr class="font-weight-bold">
                        <td class="text-left">Overall Average</td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              dataStore.videoBankCategoryStats
                                .overallDiagnosis ===
                                null ||
                              dataStore.videoBankCategoryStats
                                .overallDiagnosis === undefined
                                ? undefined
                                : dataStore.videoBankCategoryStats
                                    .overallDiagnosis >= 75
                                ? 'success'
                                : dataStore.videoBankCategoryStats
                                    .overallDiagnosis >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{
                              dataStore.videoBankCategoryStats
                                .overallDiagnosis === null ||
                              dataStore.videoBankCategoryStats
                                .overallDiagnosis === undefined
                                ? "–"
                                : dataStore.videoBankCategoryStats
                                    .overallDiagnosis + "%"
                            }}
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              dataStore.videoBankCategoryStats
                                .overallExamination ===
                                null ||
                              dataStore.videoBankCategoryStats
                                .overallExamination === undefined
                                ? undefined
                                : dataStore.videoBankCategoryStats
                                    .overallExamination >= 75
                                ? 'success'
                                : dataStore.videoBankCategoryStats
                                    .overallExamination >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{
                              dataStore.videoBankCategoryStats
                                .overallExamination === null ||
                              dataStore.videoBankCategoryStats
                                .overallExamination === undefined
                                ? "–"
                                : dataStore.videoBankCategoryStats
                                    .overallExamination + "%"
                            }}
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-chip
                            size="x-small"
                            :color="
                              dataStore.videoBankCategoryStats
                                .overallAggregate >= 75
                                ? 'success'
                                : dataStore.videoBankCategoryStats
                                    .overallAggregate >= 50
                                ? 'warning'
                                : 'error'
                            "
                            class="font-weight-bold"
                          >
                            {{
                              dataStore.videoBankCategoryStats.overallAggregate
                            }}%
                          </v-chip>
                        </td>
                        <td class="text-center">
                          <v-btn
                            size="x-small"
                            variant="text"
                            color="primary"
                            @click="openVideoBankComments('all')"
                            title="View all comments"
                          >
                            <v-icon size="small"
                              >mdi-comment-text-outline</v-icon
                            >
                            All Comments
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-table>

                  <div
                    v-if="dataStore.videoBankCategoryStats.categories.length === 0"
                    class="text-center text-caption pa-4"
                  >
                    No category data available yet. Complete some stations to see
                    your performance breakdown.
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" class="text-center mt-12">
              <v-btn
                @click="$router.push(`/sessions/${bankStore.currentBank}/new`)"
                color="primary"
                rounded="lg"
                variant="flat"
                size="large"
              >
                Practise Now
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Sessions for interview/vid banks -->
      <v-col
        cols="12"
        v-if="
          bankStore.currentBank === 'interview-bank' ||
          bankStore.currentBank === 'part-2-video-bank'
        "
      >
        <v-card
          v-motion
          :initial="{ y: 30 }"
          :enter="{ y: 0 }"
          :delay="200"
          class="pa-6"
        >
          <v-row
            v-if="dataStore.sessionsDataOrdered"
            class="px-sm-6 text-caption"
          >
            <v-col cols="12" md="8" offset-md="2" class="text-center">
              <div class="text-body-1 font-weight-bold">Sessions</div>
            </v-col>
            <v-col cols="12" md="8" offset-md="2">
              <v-row wrap>
                <v-col cols="6" sm="2"> Created At </v-col>
                <v-col class="hidden-xs" cols="2"> No. Stations </v-col>
                <v-col class="hidden-xs" cols="2"> Status </v-col>
                <v-col class="hidden-xs" cols="3"> Examiner </v-col>
                <v-col class="hidden-xs text-center" cols="3"> Actions </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="8" offset-md="2">
              <v-divider></v-divider>
            </v-col>
            <v-col
              md="8"
              offset-md="2"
              v-if="dataStore.sessionsDataOrdered.length !== 0"
              v-for="(session, idx) in dataStore.sessionsDataOrdered.slice(
                0,
                5,
              )"
              :key="idx"
              cols="12"
            >
              <v-row wrap>
                <v-col cols="6" sm="2">
                  {{ easyToReadDate(session.createdAt.seconds) }}
                </v-col>
                <v-col class="hidden-xs" cols="2">
                  {{
                    session.stationCount ??
                    session.stationIds?.length ??
                    session.stations?.length
                  }}
                </v-col>
                <v-col class="hidden-xs" cols="2">
                  {{ session?.completed ? "Completed" : "In Progress" }}
                </v-col>
                <v-col class="hidden-xs" cols="3">
                  {{ session.examinerEmail ? session.examinerEmail : "N/A" }}
                </v-col>
                <v-col cols="6" sm="3" class="text-center">
                  <v-btn
                    @click="
                      $router.push(
                        `/sessions/${bankStore.currentBank}/` + session.id,
                      )
                    "
                    color="primary"
                    rounded="lg"
                    variant="flat"
                    size="x-small"
                  >
                    {{ session?.completed ? "View" : "Resume" }}
                  </v-btn>
                  <v-icon
                    @click="
                      openSessionInNewTab(
                        `/sessions/${bankStore.currentBank}/`,
                        session.id,
                      )
                    "
                    class="ml-2"
                    size="x-small"
                    color="progress"
                    icon="mdi-open-in-new"
                  ></v-icon>
                </v-col>
              </v-row>
            </v-col>
            <v-col v-else cols="12" md="8" offset-md="2">
              <div class="text-caption text-center">No Sessions</div>
            </v-col>
            <v-col cols="12" class="text-center mt-12">
              <v-btn
                @click="$router.push('/sessions')"
                color="primary"
                rounded="lg"
                variant="flat"
                size="large"
              >
                View All Sessions
              </v-btn>
            </v-col>
          </v-row>
          <v-row v-else>
            <v-col cols="12" class="text-center">
              <div class="text-body-1 font-weight-bold">Sessions</div>
            </v-col>
            <v-col cols="12" class="text-center text-caption mt-12">
              No sessions created yet
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { useOnboardingCheck } from "@/composables/onboardingCheck";
useOnboardingCheck();
import { useDateHelpers } from "@/composables/dateHelpers";
const { easyToReadDate } = useDateHelpers();

import { usePaymentStore } from "@/store/payment";
const paymentStore = usePaymentStore();

import WalkingJSON from "@/assets/walk.json";
import BackgroundParticles from "@/components/BackgroundParticles.vue";
import BankSwitcher from "@/components/BankSwitcher.vue";
import AnnouncementCarousel from "@/components/AnnouncementCarousel.vue";
import NomogramPanel from "@/components/NomogramPanel.vue";

import { ref, computed, onMounted } from "vue";
import { useUserStore } from "@/store/user";
import { useDataStore } from "@/store/data";
import { useBankStore } from "@/store/bank";
import { useAnnouncementStore } from "@/store/announcements";
import { BANK_LABEL_BY_ID } from "@/constants/banks";
const userStore = useUserStore();
const dataStore = useDataStore();
const bankStore = useBankStore();
const announcementStore = useAnnouncementStore();

onMounted(async () => {
  announcementStore.fetchAll();
  if (bankStore.currentBank === null) {
    // Get all subscriptions and find the one with earliest expiry
    await userStore.getBasicUserData();
    const subscriptions = userStore.basic.subscriptions;
    if (subscriptions) {
      let earliestExpiryBank = null;
      let earliestExpiryDate = null;

      // Loop through all subscriptions
      Object.entries(subscriptions).forEach(([bank, subscription]) => {
        if (subscription.val) {
          const expiryDate = new Date(subscription.date);
          if (!earliestExpiryDate || expiryDate < earliestExpiryDate) {
            earliestExpiryDate = expiryDate;
            earliestExpiryBank = bank;
          }
        }
      });

      // Set the bank with earliest expiry as current bank
      if (earliestExpiryBank) {
        bankStore.currentBank = earliestExpiryBank;
      } else {
        // If no active subscriptions, default to part-2-question-bank
        bankStore.currentBank = "part-2-question-bank";
      }
    } else {
      // If no subscriptions at all, default to part-2-question-bank
      bankStore.currentBank = "part-2-question-bank";
    }
    localStorage.setItem("eye-academy-last-bank-used", bankStore.currentBank);
  }
});

const loading = ref(false);

const secondsLeftTillExam = computed(() => {
  if (!userStore.basic.examDate) return null;
  let currentTime = Date.now();
  let examTime = new Date(userStore.basic.examDate.seconds * 1000);
  let timeDifference = examTime - currentTime;
  let secondsTillExam = Math.floor(timeDifference);
  return secondsTillExam;
});

const examDateUKFormat = computed(() => {
  if (!userStore.basic.examDate) return null;
  let examTime = new Date(userStore.basic.examDate.seconds * 1000);
  // UK date with day and time
  let examDate = examTime.toLocaleString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return examDate;
});

const examDateUpdateLoading = ref(false);
const editingExamDate = ref(false);
const examDate = ref(null);
const examDateHint = ref("");
const examDateNotValid = (date) => {
  if (!date) return true;
  const today = new Date();
  const examDate = new Date(date);
  const hundredYearsFromNow = new Date();
  hundredYearsFromNow.setFullYear(today.getFullYear() + 100);
  if (examDate < today) {
    examDateHint.value = "Must be in the future!";
    return true;
  } else if (examDate > hundredYearsFromNow) {
    examDateHint.value = "Will you even be alive?!";
    return true;
  } else {
    examDateHint.value = "";
  }
};
const examDateValid = computed(() => {
  return !examDateNotValid(examDate.value);
});
const changeExamDate = async () => {
  examDateUpdateLoading.value = true;
  await userStore.updateExamDate(examDate.value);
  // refetch user date
  await userStore.getBasicUserData();
  editingExamDate.value = false;
  examDateUpdateLoading.value = false;
};

const openSessionInNewTab = (path, sessionId) => {
  window.open(path + sessionId, "_blank");
};

// Stats
import { useStatsStore } from "@/store/stats";
const statsStore = useStatsStore();

const perc = (num, total) => {
  if (total == 0 || isNaN(num) || isNaN(total)) {
    return 0;
  }
  return ((num / total) * 100).toFixed(1);
};

const getBankDisplayName = (bankId) => BANK_LABEL_BY_ID[bankId] || bankId;

const isSubscribedToCurrentBank = computed(() => {
  const bankId = bankStore.currentBank;
  if (!bankId) return false;
  return Boolean(paymentStore.isSubscribed?.[bankId]?.val);
});

const announcementsForCurrentBank = computed(() => {
  if (!bankStore.currentBank) {
    return [];
  }
  const announcements = announcementStore.getActiveForBank(
    bankStore.currentBank,
  );
  return announcements.filter((announcement) => {
    if (announcement?.subscribersOnly && !isSubscribedToCurrentBank.value) {
      return false;
    }
    return true;
  });
});

const parentCats = computed(() => {
  // Early return if categories data isn't loaded yet
  if (!dataStore.categoriesData?.length) return [];

  // Create a map of parent categories and their children
  const parentMap = dataStore.categoriesData.reduce((acc, category) => {
    // Skip categories with 0 questions
    if (category.stats.numberOfQuestions === 0) return acc;

    const parent = category.parent || "Other";
    if (!acc[parent]) {
      acc[parent] = {
        parent,
        children: [],
        totalQuestions: 0,
      };
    }
    acc[parent].children.push(category.name);
    acc[parent].totalQuestions += category.stats.numberOfQuestions;
    return acc;
  }, {});

  // Filter out parent categories with 0 total questions
  return Object.values(parentMap)
    .filter((group) => group.totalQuestions > 0)
    .map((group) => ({
      parent: group.parent,
      children: group.children.sort((a, b) => {
        // Sort children by number of questions (descending)
        return (
          categoryDataObject.value[b].stats.numberOfQuestions -
          categoryDataObject.value[a].stats.numberOfQuestions
        );
      }),
      totalQuestions: group.totalQuestions,
    }))
    .sort((a, b) => {
      // Sort parents by total questions (descending)
      return b.totalQuestions - a.totalQuestions;
    });
});

const categoryDataObject = computed(() => {
  const dict = {};
  dataStore.categoriesData.forEach((cat) => {
    dict[cat.name] = cat;
  });
  return dict;
});

const completionDataCache = ref({});
const getChildrenCompletionData = (children) => {
  const cacheKey = children.join("-");
  if (completionDataCache.value[cacheKey]) {
    return completionDataCache.value[cacheKey];
  }
  try {
    let total = 0;
    let completed = 0;
    children.forEach((cat) => {
      const catStats = getUserCatStat(categoryDataObject.value[cat].id);
      total += categoryDataObject.value[cat].stats.numberOfQuestions;
      completed += catStats.uniqueQuestionsAttempted;
    });
    const result = {
      total,
      completed,
      perc: perc(completed, total),
    };
    completionDataCache.value[cacheKey] = result;
    return result;
  } catch (e) {
    return {
      total: 0,
      completed: 0,
      perc: 0,
    };
  }
};

const getUserCatStat = (catId) => {
  const catStats = statsStore.userStats?.categories?.[catId];
  if (!catStats) {
    return {
      questionsAttempted: 0,
      questionsCorrect: 0,
      uniqueQuestionsAttempted: 0,
    };
  }
  return catStats;
};
const mobile = computed(() => {
  return window.innerWidth < 600;
});

const openDomainComments = (domainKey) => {
  // Convert to lowercase, split by spaces and hyphens, then join with plus signs
  const lowerDomain = domainKey.toLowerCase();
  const formattedDomain = lowerDomain
    .split(/[\s-]+/)
    .filter((part) => part.length > 0)
    .join("+");
  const commentsUrl = `/comments/interview-bank/?domain=${formattedDomain}`;
  window.open(commentsUrl, "_blank");
};

const openVideoBankComments = (category) => {
  // Convert to lowercase, split by spaces and hyphens, then join with plus signs
  const lowerCat = category.toLowerCase();
  const formattedCategory = lowerCat
    .split(/[\s-]+/)
    .filter((part) => part.length > 0)
    .join("+");
  const commentsUrl = `/comments/part-2-video-bank/?domain=${formattedCategory}`;
  window.open(commentsUrl, "_blank");
};
</script>

<style scoped>
.gentle-grad {
  background-image: linear-gradient(
    to right,
    #03045e,
    rgb(30, 64, 175),
    rgb(17, 24, 39)
  );
}

.nice-grad-background {
  background: linear-gradient(
    to right,
    rgb(236, 72, 153),
    rgb(239, 68, 68),
    rgb(234, 179, 8)
  ) !important;
}
</style>
